<template>
  <bxs-bottom-sheets
    v-model="isShow"
    class="pdf-bottom-sheets"
    title="选择文档导出类型"
    height="middle"
    :use-b-scroll="false"
    :prevent-scroll-through="true"
  >
    <!-- 格式信息 -->
    <format-selection v-bind.sync="format" :miniprogram="miniprogram" />

    <!-- 内容信息 -->
    <content-selection v-model="content" :options="contentOptions" />

    <!-- 客户信息 -->
    <customer-remarks v-bind.sync="customer" />

    <div slot="footer" class="bottom-link">
      <a
        class="to-table-report"
        href="javascript:;"
        @click="handleDetermine"
      >确定</a>
    </div>
  </bxs-bottom-sheets>
</template>

<script>
import qs from 'qs'
import { get as _get, cloneDeep } from 'lodash'
import { isWeCom, isWeEnterprise, isMiniBankInsurance } from 'base/config'
import { baseUrl, pbfUrl } from 'api/config'
import { getExportOption } from 'api/index'
import {
  getUrlParam,
  buriedSubmit,
  parseTime,
  getLocalStorage,
  setLocalStorage
} from 'common/js/common'
import { BottomSheets, Icon } from 'bxs-ui-vue'
import FormatSelection from './components/FormatSelection'
import ContentSelection from './components/ContentSelection'
import CustomerRemarks from './components/CustomerRemarks'
import {
  REMAKRS_OPTIONS,
  STORAGE_KEY,
  STORAGE_NG_TIME,
  CONTENT_OPTIONS
} from './components/config'

export default {
  name: 'ExportSelection',
  components: {
    [Icon.name]: Icon,
    [BottomSheets.name]: BottomSheets,
    FormatSelection,
    ContentSelection,
    CustomerRemarks
  },
  props: {
    value: Boolean,
    miniprogram: Boolean,
    isFamily: Boolean, // 预留(家庭计划书)
    cardUuid: String // 预留(家庭计划书)
  },
  data () {
    this.uuid = getUrlParam(this.isFamily ? 'familyId' : 'uuid')
    return {
      // (api)导出配置
      exportOption: {},
      // 格式选择
      format: {
        typeval: 0,
        coverval: 1
      },
      // 内容配置
      contentOptions: [],
      // 内容选择
      content: {
        introduceCompanyId: null,
        ideaId: null,
        diseaseShow: null,
        interestType: null,
        problemShow: null,
        cardType: 1
      },
      // 客户选择
      customer: {
        customerName: '',
        customerMessage: REMAKRS_OPTIONS[0]
      }
    }
  },
  computed: {
    isShow: {
      get () {
        return this.value
      },
      set (v) {
        this.$emit('input', v)
      }
    }
  },
  watch: {
    isShow (v) {
      const { uuid, exportOption } = this
      if (!v || (exportOption && Object.keys(exportOption).length)) return
      getExportOption({ uuid }).then(
        ({ data: { success, data } } = { data: {} }) => {
          if (!success) return
          this.exportOption = data
          this.contentOptions = this.formatOptions()
        }
      )
    }
  },
  created () {
    this.getStorage()
  },
  methods: {
    getStorage () {
      let exportValue = getLocalStorage(STORAGE_KEY)
      exportValue = JSON.parse(exportValue)
      if (!exportValue) return

      // 以下if判断，是为了兼容部分客户端可能已有缓存。By 20230213
      // 如果你看到以下代码是1~3月后，可以考虑删除，改用下面这行注释的代码。
      // exportValue.content && (this.content = exportValue.content)
      if (exportValue.content) {
        for (const k in exportValue.content) {
          if (k === 'introduceCompanyId') {
            continue
          }
          this.content[k] = exportValue.content[k]
        }
      }
      exportValue.format && (this.format = exportValue.format)
      exportValue.customer && (this.customer = exportValue.customer)
    },
    setStorage () {
      const { format, content, customer } = this
      setLocalStorage(
        STORAGE_KEY,
        JSON.stringify({
          format,
          content: {
            ...content,
            introduceCompanyId: null
          },
          customer: {
            ...customer,
            customerName: ''
          }
        }),
        STORAGE_NG_TIME
      )
    },
    formatOptions () {
      const {
        exportOption: {
          diseaseFlag,
          interestFlag,
          introduceCompanyId,
          introduceCompanyName
        },
        content,
        format: { coverval }
      } = this
      let copyOptions = JSON.parse(JSON.stringify(CONTENT_OPTIONS))
      copyOptions = copyOptions.map(item => {
        if (
          item.value === 'introduceShow' &&
          item.children &&
          Array.isArray(item.children.options) &&
          introduceCompanyId &&
          introduceCompanyName
        ) {
          const newCompany = {
            value: introduceCompanyId,
            label: introduceCompanyName
          }
          if (item.children.options.length === 2 && !item.children.options.find(o => o.value === introduceCompanyId)) {
            item.children.options.splice(1, 0, newCompany)
          }
          // 当前用户公司与本地缓存公司（上一次选取）不一致
          // 设置为当前用户公司
          if (
            item.children.options.every(
              op => op.value !== content.introduceCompanyId
            )
          ) {
            this.content.introduceCompanyId = introduceCompanyId
          }
          item.children.value = introduceCompanyId
        }
        return item
      })
      return copyOptions.filter(item => {
        if (!diseaseFlag && item.value === 'diseaseShow') {
          this.content.diseaseShow = null
          return false
        }
        if (!interestFlag && item.value === 'interestShow') {
          this.content.interestType = null
          return false
        }
        return true
      })
    },
    formatExportParams () {
      const {
        content,
        customer: { customerName, customerMessage },
        format: { coverval }
      } = this
      const params = {
        coverType: coverval,
        customerName: encodeURIComponent(customerName),
        customerMessage: encodeURIComponent(customerMessage)
      }
      if (isWeCom || isWeEnterprise || isMiniBankInsurance) {
        content.cardType = -1
      }
      Object.keys(content).forEach(item => {
        const value = content[item]
        if (value !== null) {
          params[item] = value
        }
      })
      return params
    },
    async handleDetermine () {
      const { uuid, format, miniprogram } = this
      const isWord = format.typeval === 1
      const title = `${document.title}${parseTime(new Date(), '{h}-{i}-{s}')}`
      const params = cloneDeep(this.formatExportParams())
      params.resultUuid = uuid
      const exportApi = `${baseUrl}/planBook/export/pb/export?`
      if (isWord) {
        params.word = true
        buriedSubmit('word')
      } else {
        buriedSubmit(['pdf_jianyue', 'pdf_shangwu', 'pdf_weiyi', 'pdf_shishang'][format.coverval - 1])
      }
      this.setStorage()
      // 企微（网页预览pdf）
      if (!isWord && (isWeCom || miniprogram)) {
        const toURL = `${exportApi}${qs.stringify(params)}`
        console.log('导出文档：', toURL)
        location.href = `${pbfUrl}/planBook/planbookAccessories/pdf-viewer/index.html?file=${encodeURIComponent(toURL)}&title=${title}`
        return
      }

      const _saveInteractionSetting = this.$listeners.saveInteractionSetting
      if (_saveInteractionSetting) {
        const newResultUuid = await _saveInteractionSetting()
        if (newResultUuid) {
          params.resultUuid = newResultUuid
        }
      }
      const _params = cloneDeep(params)
      _params.customerMessage = decodeURIComponent(_params.customerMessage)
      _params.customerName = decodeURIComponent(_params.customerName)
      const toURL = `${exportApi}${qs.stringify(_params)}`
      console.log('导出文档：', toURL)
      const _HANDLE_FILE_DOWNLOAD_AND_SHARE = window.appBridge && window.appBridge.checkAppFeature('HANDLE_FILE_DOWNLOAD_AND_SHARE')
      if (_HANDLE_FILE_DOWNLOAD_AND_SHARE) {
        window.appBridge.handleFile({
          url: toURL,
          title,
          action: isWord ? 'downloadAndShare' : 'sharePdf'
        })
      } else {
        location.href = toURL
      }
    }
  }
}
</script>